import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { createSecureLinkClient } from '@roc-capital/secure-link';
import { useSecureLinkSession } from '@roc-capital/secure-link/react';
import { useBaseStore } from '@roc/feature-app-core';
import { PublicScopeOfWorkForm, ScopeOfWorkV2Form } from '@roc/feature-sow-v2';
import { useStore } from '../../hooks';
import { SecureLinkSessionStatusPage } from '../scopeofWorkRevision';
import { ScopeOfWorkV2Service } from '../../services/scopeOfWorkV2Service';
import { SecureLinkCreateScopeOfWorkService, SecureLinkScopeOfWorkService } from '../../services/secureLinkScopeOfWorkService';

const useSecureLinkClient = () => {
  const { environmentStore } = useBaseStore();
  return useMemo(
    () => createSecureLinkClient({ apiBaseUrl: environmentStore.environmentVariables.restApiBaseURL ?? '' }),
    []
  );
};

/**
 * The shared scope of work for secure links: /link/scope-of-work. The contractor arrives here from the platform
 * landing page (/platform-app/link#token), which started the session; every call that reads or changes the scope of
 * work is authorized by that session, and the scope of work comes from it on the server. Replaces
 * /scope-of-work-v2/:encodedParams, which carried the scope of work id, lender id and a username in base64.
 */
export const SecureLinkScopeOfWorkPage = observer(() => {
  const client = useSecureLinkClient();
  const { status, request, retry } = useSecureLinkSession(client, { purpose: 'SOW_SHARE' });

  const { scopeOfWorkV2Store } = useStore();

  // A layout effect, so it runs before the scope of work's first fetch on the same commit.
  useLayoutEffect(() => {
    scopeOfWorkV2Store.setScopeOfWorkService(new SecureLinkScopeOfWorkService(request));
    return () => scopeOfWorkV2Store.setScopeOfWorkService(new ScopeOfWorkV2Service());
  }, [scopeOfWorkV2Store, request]);

  useEffect(() => {
    if (status !== 'active') return;
    scopeOfWorkV2Store.reset();
    scopeOfWorkV2Store.fetchSecureLinkScopeOfWork();
    return () => {
      scopeOfWorkV2Store.reset();
    };
  }, [status]);

  useEffect(() => {
    scrollTo(0, 0);
  }, [scopeOfWorkV2Store.activeStep]);

  if (status === 'active') {
    return <ScopeOfWorkV2Form store={scopeOfWorkV2Store} />;
  }

  return <SecureLinkSessionStatusPage status={status} retry={retry} />;
});

/**
 * The lender's create scope of work link for secure links: /link/create-scope-of-work. Only the submit goes through
 * the session, which holds the lender and the lender user; the template, categories, submission errors and export
 * stay on the public endpoints. Replaces /create-scope-of-work/:encodedParams, which carried the lender id and a
 * username in base64. The short link /create-scope-of-work (no params) stays public.
 */
export const SecureLinkCreateScopeOfWorkPage = observer(() => {
  const client = useSecureLinkClient();
  const { status, request, retry } = useSecureLinkSession(client, { purpose: 'SOW_CREATE' });

  const { publicscopeOfWorkV2Store } = useStore();

  // A layout effect, so the form's store has the session's service before the form's own effects run.
  useLayoutEffect(() => {
    publicscopeOfWorkV2Store.setScopeOfWorkService(new SecureLinkCreateScopeOfWorkService(request));
    return () => publicscopeOfWorkV2Store.setScopeOfWorkService(new ScopeOfWorkV2Service());
  }, [publicscopeOfWorkV2Store, request]);

  if (status === 'active') {
    return <PublicScopeOfWorkForm secureLink />;
  }

  return <SecureLinkSessionStatusPage status={status} retry={retry} />;
});
